import { useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ImageAnnotation } from '@annotorious/react';
import { toast } from 'sonner';

import {
  useCreateDetection,
  useDeleteDetection,
  usePatchDetection,
  WorkspaceDetectionsResponseOutputItemsItemLabel as Label,
  WorkspaceDetectionsResponseOutputItemsItemSource as Source,
} from '@/api/generated';
import {
  type Detection,
  type DetectionLabel,
  detectionToAnnotation,
  getAnnotationProperties,
  getAnnotationRect,
  getNextAnnotation,
  sortAnnotationsIntoReadingOrder,
} from '@/lib/annotations';
import { useAuth } from '@/providers/auth-provider';

interface UseShelfDetailWorkspaceArgs {
  customerId: number;
  submissionId: string;
  shelveId: string;
}

interface WorkspaceState {
  annotations: Array<ImageAnnotation>;
  /**
   * Multi-selection set in click order; the last entry is the primary
   * selection driving the side panel and label actions.
   */
  selectedIds: Array<string>;
}

interface SelectedAnnotationLabelPatch {
  label: DetectionLabel;
  skuMetadataId?: string | null;
}

/**
 * One undoable labeling action: the previous label/SKU of every box it
 * touched. A bulk multi-select patch is a single entry, so one Undo press
 * restores the whole batch. Kept in client memory only (per shelf, per
 * session) — the restores themselves persist through the normal PATCH.
 */
type UndoEntry = Array<{
  id: string;
  label: DetectionLabel;
  skuMetadataId?: string | null;
}>;

const UNDO_HISTORY_LIMIT = 100;

type WorkspaceAction =
  | { type: 'selectAnnotation'; id: string | null }
  | { type: 'toggleAnnotation'; id: string }
  | { type: 'selectAnnotations'; ids: Array<string> }
  | {
      type: 'labelResults';
      deletedIds: Array<string>;
      patchedIds: Array<string>;
      patch: SelectedAnnotationLabelPatch;
    }
  | { type: 'restoreLabels'; items: UndoEntry }
  | { type: 'removeAnnotation'; id: string }
  | {
      type: 'commitDrawnAnnotation';
      tempId: string;
      annotation: ImageAnnotation;
    }
  | { type: 'annotationsChanged'; next: Array<ImageAnnotation> };

const repairSelectedIds = (
  current: Array<string>,
  next: Array<ImageAnnotation>
): Array<string> => {
  const existing = new Set(next.map((annotation) => annotation.id));
  const repaired = current.filter((id) => existing.has(id));

  if (repaired.length === 0) return next[0] ? [next[0].id] : [];
  // Keep the array identity stable when nothing was dropped, so the
  // selection-sync effects downstream don't re-fire on unrelated changes.
  return repaired.length === current.length ? current : repaired;
};

const workspaceReducer = (
  state: WorkspaceState,
  action: WorkspaceAction
): WorkspaceState => {
  switch (action.type) {
    case 'selectAnnotation': {
      const ids = action.id ? [action.id] : [];
      // Bail with the same state on a no-op replace: the annotator echoes
      // programmatic selection changes back, and a fresh array identity here
      // would re-trigger the sync effect in an endless loop.
      const unchanged =
        state.selectedIds.length === ids.length &&
        state.selectedIds[0] === ids[0];
      return unchanged ? state : { ...state, selectedIds: ids };
    }
    case 'toggleAnnotation':
      return {
        ...state,
        selectedIds: state.selectedIds.includes(action.id)
          ? state.selectedIds.filter((id) => id !== action.id)
          : [...state.selectedIds, action.id],
      };
    case 'selectAnnotations': {
      // Rubber-band selection is additive, like Shift + click: merge the band
      // hits into the set so consecutive bands build one selection. Keep the
      // state identity when nothing new was hit (see the no-op bail above).
      const selected = new Set(state.selectedIds);
      const added = action.ids.filter((id) => !selected.has(id));
      return added.length === 0
        ? state
        : { ...state, selectedIds: [...state.selectedIds, ...added] };
    }
    // Applies a resolved label batch over the reducer's CURRENT annotations —
    // not a snapshot captured before the API round-trip — so boxes drawn or
    // changed while the PATCHes were in flight survive. Boxes whose call
    // failed stay as-is; the selection advances to the next box needing
    // attention so the just-labeled boxes are skipped.
    case 'labelResults': {
      const deleted = new Set(action.deletedIds);
      const patched = new Set(action.patchedIds);
      const annotations = state.annotations
        .filter((annotation) => !deleted.has(annotation.id))
        .map((annotation) =>
          patched.has(annotation.id)
            ? {
                ...annotation,
                properties: { ...annotation.properties, ...action.patch },
              }
            : annotation
        );
      const primary = state.selectedIds[state.selectedIds.length - 1];
      const selectedIds =
        primary && patched.size > 0 && annotations.length > 0
          ? [getNextAnnotation(annotations, primary).id]
          : repairSelectedIds(state.selectedIds, annotations);
      return { annotations, selectedIds };
    }
    // Undo: restores the items' previous label/SKU (over the current
    // annotations, see above) and jumps the selection back to the restored
    // boxes, replacing whatever was selected.
    case 'restoreLabels': {
      const previousById = new Map(action.items.map((item) => [item.id, item]));
      const annotations = state.annotations.map((annotation) => {
        const previous = previousById.get(annotation.id);
        return previous
          ? {
              ...annotation,
              properties: {
                ...annotation.properties,
                label: previous.label,
                skuMetadataId: previous.skuMetadataId,
              },
            }
          : annotation;
      });
      const existing = new Set(annotations.map((annotation) => annotation.id));
      return {
        annotations,
        selectedIds: action.items
          .map((item) => item.id)
          .filter((id) => existing.has(id)),
      };
    }
    // Drops one box (deleted or rolled back) from the current annotations,
    // so boxes drawn while the delete round-trip was in flight survive.
    case 'removeAnnotation': {
      const annotations = state.annotations.filter(
        (annotation) => annotation.id !== action.id
      );
      return {
        annotations,
        selectedIds: repairSelectedIds(state.selectedIds, annotations),
      };
    }
    // Swaps a freshly persisted drawn box in for its temp placeholder (over
    // the current annotations, see above) and selects it.
    case 'commitDrawnAnnotation': {
      const annotations = state.annotations.map((annotation) =>
        annotation.id === action.tempId ? action.annotation : annotation
      );
      return { annotations, selectedIds: [action.annotation.id] };
    }
    case 'annotationsChanged': {
      const next = action.next;
      return {
        annotations: next,
        selectedIds: repairSelectedIds(state.selectedIds, next),
      };
    }
    default:
      return state;
  }
};

export const useShelfDetailWorkspace = (
  initialAnnotations: Array<ImageAnnotation>,
  { customerId, submissionId, shelveId }: UseShelfDetailWorkspaceArgs
) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { mutateAsync: createDetection } = useCreateDetection();
  const { mutateAsync: patchDetection } = usePatchDetection();
  const { mutateAsync: deleteDetection } = useDeleteDetection();
  const isPersisting = useRef(false);
  const isUndoing = useRef(false);
  // Undo and label/create/delete flows exclude each other: an in-flight
  // label hasn't pushed its history entry yet (undo would revert an older
  // action), and its PATCH could land after undo's, silently re-applying the
  // label the user just took back. A counter, not a boolean: labeling calls
  // can overlap (auto-advance makes two quick SKU clicks easy), and the
  // first one finishing must not unblock undo while the second is in flight.
  const pendingMutations = useRef(0);
  const [undoHistory, setUndoHistory] = useState<Array<UndoEntry>>([]);
  const [state, dispatch] = useReducer(
    workspaceReducer,
    initialAnnotations,
    (initial): WorkspaceState => {
      // Start on the first box that still needs attention — in reading order
      // (top-left first), not API order, which is by detection id and jumps
      // around the image. Fall back to the first box when everything is
      // already labeled.
      const ordered = sortAnnotationsIntoReadingOrder(initial);
      const firstUnannotated = ordered.find(
        (annotation) =>
          getAnnotationProperties(annotation).label === Label.UNANNOTATED
      );
      const selected = firstUnannotated ?? ordered[0];
      return {
        annotations: initial,
        selectedIds: selected ? [selected.id] : [],
      };
    }
  );
  // Primary selection: the most recently added box drives the side panel and
  // label actions while a multi-selection is active.
  const selectedId = state.selectedIds[state.selectedIds.length - 1] ?? null;

  const selectAnnotation = useCallback((id: string | null) => {
    dispatch({ type: 'selectAnnotation', id });
  }, []);

  const toggleAnnotation = useCallback((id: string) => {
    dispatch({ type: 'toggleAnnotation', id });
  }, []);

  const selectAnnotations = useCallback((ids: Array<string>) => {
    dispatch({ type: 'selectAnnotations', ids });
  }, []);

  const onAnnotationsChange = useCallback((next: Array<ImageAnnotation>) => {
    dispatch({ type: 'annotationsChanged', next });
  }, []);

  const patchDetectionLabel = useCallback(
    async (
      detectionId: string,
      { label, skuMetadataId }: SelectedAnnotationLabelPatch
    ) =>
      patchDetection({
        detectionId,
        data: {
          detectionId,
          label,
          skuMetadataId: skuMetadataId ?? undefined,
          userId: '',
        },
      }),
    [patchDetection]
  );

  // Removes a box from local state; the annotator canvas syncs via the bridge.
  const removeAnnotationLocally = useCallback((id: string) => {
    dispatch({ type: 'removeAnnotation', id });
  }, []);

  // Removing a user-generated box deletes its detection outright rather than
  // marking it REMOVED (that label is reserved for model detections of products
  // missing from the shelf). A temp box was never persisted, so there is nothing
  // to delete server-side — just drop it locally.
  const deleteUserDetection = useCallback(
    async (detectionId: string, temp: boolean) => {
      if (!temp) {
        try {
          await deleteDetection({ detectionId });
        } catch {
          toast.error(t('shelfDetail.deleteDetectionFailed'));
          return;
        }
      }

      removeAnnotationLocally(detectionId);
    },
    [deleteDetection, removeAnnotationLocally, t]
  );

  const deleteSelectedAnnotation = useCallback(async () => {
    if (!selectedId) return;
    // The selection is about to jump to the boxes the undo restores — acting
    // on the current one would hit the wrong box. Tell the user instead of
    // silently ignoring the input.
    if (isUndoing.current) {
      toast.info(t('shelfDetail.undoInProgress'));
      return;
    }

    const selected = state.annotations.find(
      (annotation) => annotation.id === selectedId
    );
    if (!selected) return;

    const { source, temp } = getAnnotationProperties(selected);
    if (!temp && source !== Source.USER) return;

    pendingMutations.current += 1;
    try {
      await deleteUserDetection(selectedId, temp);
    } finally {
      pendingMutations.current -= 1;
    }
  }, [deleteUserDetection, selectedId, state.annotations, t]);

  // Applies the patch to every committed detection in the selection — acting
  // on the whole set is the point of multi-select, and a confirmed SKU must
  // land on all selected boxes even when it never appeared in a box's own
  // prediction list (the server writes sku_id directly, without checking
  // product_recognitions). REMOVED deletes user-drawn boxes outright instead
  // of labeling them, mirroring the single-selection rule. Temp boxes are
  // skipped: they have no backend detection to patch.
  const labelCommittedSelection = useCallback(
    async (patch: SelectedAnnotationLabelPatch) => {
      const selectedSet = new Set(state.selectedIds);
      const selected = state.annotations.filter(
        (annotation) =>
          selectedSet.has(annotation.id) &&
          !getAnnotationProperties(annotation).temp
      );

      const toDelete =
        patch.label === Label.REMOVED
          ? selected.filter(
              (annotation) =>
                getAnnotationProperties(annotation).source === Source.USER
            )
          : [];
      const deleteIdSet = new Set(toDelete.map((annotation) => annotation.id));
      const toPatch = selected.filter(
        (annotation) => !deleteIdSet.has(annotation.id)
      );

      const [deleteResults, patchResults] = await Promise.all([
        Promise.allSettled(
          toDelete.map(async (annotation) =>
            deleteDetection({ detectionId: annotation.id })
          )
        ),
        Promise.allSettled(
          toPatch.map(async (annotation) =>
            patchDetectionLabel(annotation.id, patch)
          )
        ),
      ]);

      const deletedIds = new Set(
        toDelete
          .filter((_, index) => deleteResults[index]?.status === 'fulfilled')
          .map((annotation) => annotation.id)
      );
      const patchedIds = new Set(
        toPatch
          .filter((_, index) => patchResults[index]?.status === 'fulfilled')
          .map((annotation) => annotation.id)
      );

      if (deletedIds.size < toDelete.length) {
        toast.error(t('shelfDetail.deleteDetectionFailed'));
      }

      // Record the pre-patch label/SKU of every box that was actually patched
      // as one undo step. Deletions are not undoable (restoring one would
      // require re-creating the detection server-side), so they are left out.
      if (patchedIds.size > 0) {
        const entry: UndoEntry = toPatch
          .filter((annotation) => patchedIds.has(annotation.id))
          .map((annotation) => {
            const { label, skuMetadataId } =
              getAnnotationProperties(annotation);
            return { id: annotation.id, label, skuMetadataId };
          });
        setUndoHistory((history) => [
          ...history.slice(-(UNDO_HISTORY_LIMIT - 1)),
          entry,
        ]);
      }

      // One dispatch for the whole batch; the reducer applies it over the
      // current annotations and advances the selection.
      dispatch({
        type: 'labelResults',
        deletedIds: [...deletedIds],
        patchedIds: [...patchedIds],
        patch,
      });
    },
    [
      deleteDetection,
      patchDetectionLabel,
      state.annotations,
      state.selectedIds,
      t,
    ]
  );

  // Freshly drawn box: validate geometry, persist it, then swap the temp box for
  // the real one. Rolls the local box back if persistence fails.
  const createAndLabelDetection = useCallback(
    async (selected: ImageAnnotation, patch: SelectedAnnotationLabelPatch) => {
      // Reject degenerate geometry before hitting the API.
      const { x, y, w, h } = getAnnotationRect(selected);
      if (w <= 0 || h <= 0) {
        removeAnnotationLocally(selected.id);
        return;
      }

      // Guard against a double-click creating two detections for the same box.
      if (isPersisting.current) return;
      isPersisting.current = true;

      try {
        const created: Detection = await createDetection({
          data: {
            customerId,
            submissionId,
            shelveId,
            x1: x,
            y1: y,
            x2: x + w,
            y2: y + h,
          },
        });
        await patchDetectionLabel(created.id, patch);

        // Swap the temp box for the real one and keep it selected. The
        // reducer applies it over the current annotations, so boxes drawn
        // while the create round-trip was in flight survive.
        dispatch({
          type: 'commitDrawnAnnotation',
          tempId: selected.id,
          annotation: detectionToAnnotation({ ...created, ...patch }),
        });
      } catch {
        removeAnnotationLocally(selected.id);
        toast.error(t('shelfDetail.createDetectionFailed'));
      } finally {
        isPersisting.current = false;
      }
    },
    [
      createDetection,
      customerId,
      patchDetectionLabel,
      removeAnnotationLocally,
      shelveId,
      submissionId,
      t,
    ]
  );

  // Routes a label change to the right operation. A temp primary box (always
  // a lone or newest selection — drawing replaces the set) is persisted or
  // dropped; committed boxes are labeled in bulk across the whole selection.
  const labelSelectedAnnotation = useCallback(
    async (patch: SelectedAnnotationLabelPatch) => {
      if (!selectedId || !user) return;
      // The selection is about to jump to the boxes the undo restores — the
      // click was aimed at a selection that no longer holds. Tell the user
      // instead of silently ignoring the input.
      if (isUndoing.current) {
        toast.info(t('shelfDetail.undoInProgress'));
        return;
      }

      const selected = state.annotations.find(
        (annotation) => annotation.id === selectedId
      );
      if (!selected) return;

      const { source, temp } = getAnnotationProperties(selected);

      pendingMutations.current += 1;
      try {
        if (temp) {
          if (patch.label === Label.REMOVED && source === Source.USER) {
            await deleteUserDetection(selectedId, true);
            return;
          }
          await createAndLabelDetection(selected, patch);
          return;
        }

        await labelCommittedSelection(patch);
      } finally {
        pendingMutations.current -= 1;
      }
    },
    [
      createAndLabelDetection,
      deleteUserDetection,
      labelCommittedSelection,
      state.annotations,
      selectedId,
      t,
      user,
    ]
  );

  // Reverts the most recent labeling action: PATCHes every box of the entry
  // back to its previous label/SKU, mirrors that in local state, and jumps the
  // selection back to the restored boxes so a correction is one click away.
  const undoLastLabel = useCallback(async () => {
    // Ctrl+Z can repeat faster than the PATCHes resolve; one undo at a time.
    if (isUndoing.current) return;
    // A label/create/delete still in flight hasn't pushed its history entry
    // yet and will dispatch from a pre-undo snapshot — undoing now would
    // revert an older action and then be clobbered by that snapshot. This is
    // the "misclick → immediately Ctrl+Z" flow, so tell the user to retry
    // rather than silently swallowing the press.
    if (pendingMutations.current > 0) {
      toast.info(t('shelfDetail.savingInProgress'));
      return;
    }

    // Boxes can have been deleted since they were labeled; their history
    // items can't be restored. Walk back to the newest entry that still has
    // restorable boxes, marking exhausted entries for removal along the way.
    const existingIds = new Set(
      state.annotations.map((annotation) => annotation.id)
    );
    const exhausted = new Set<UndoEntry>();
    let entryToUndo: UndoEntry | undefined;
    let items: UndoEntry | undefined;
    for (let index = undoHistory.length - 1; index >= 0 && !items; index--) {
      const entry = undoHistory[index];
      if (!entry) continue;
      const restorable = entry.filter((item) => existingIds.has(item.id));
      if (restorable.length > 0) {
        entryToUndo = entry;
        items = restorable;
      } else {
        exhausted.add(entry);
      }
    }
    // Update by entry identity via a functional update: a labeling PATCH can
    // resolve while the undo PATCHes are awaited, appending a new entry that
    // an absolute overwrite would silently wipe. `replaceWith` swaps the
    // undone entry for its not-yet-restored remainder (empty = remove), so a
    // partly failed undo keeps the failed boxes retryable.
    const updateHistory = (replaceWith: UndoEntry = []) => {
      setUndoHistory((history) =>
        history
          .map((entry) => (entry === entryToUndo ? replaceWith : entry))
          .filter((entry) => !exhausted.has(entry) && entry.length > 0)
      );
    };
    if (!items) {
      updateHistory();
      return;
    }

    isUndoing.current = true;
    try {
      const results = await Promise.allSettled(
        items.map(async ({ id, label, skuMetadataId }) =>
          patchDetectionLabel(id, { label, skuMetadataId })
        )
      );
      const restoredIds = new Set(
        items
          .filter((_, index) => results[index]?.status === 'fulfilled')
          .map((item) => item.id)
      );

      if (restoredIds.size < items.length) {
        toast.error(t('shelfDetail.undoFailed'));
      }
      // Boxes whose PATCH failed stay in the history (as the entry's
      // remainder) so the next Undo press retries exactly them.
      updateHistory(items.filter((item) => !restoredIds.has(item.id)));
      if (restoredIds.size === 0) return;

      // The reducer applies the restore over its current annotations — a box
      // drawn while the undo PATCHes were in flight must survive.
      dispatch({
        type: 'restoreLabels',
        items: items.filter((item) => restoredIds.has(item.id)),
      });
    } finally {
      isUndoing.current = false;
    }
  }, [patchDetectionLabel, state.annotations, t, undoHistory]);

  // Only entries with at least one still-existing box count — otherwise the
  // Undo button would sit enabled while a press could not restore anything.
  const canUndo = useMemo(() => {
    const existing = new Set(
      state.annotations.map((annotation) => annotation.id)
    );
    return undoHistory.some((entry) =>
      entry.some((item) => existing.has(item.id))
    );
  }, [state.annotations, undoHistory]);

  return {
    annotations: state.annotations,
    selectedIds: state.selectedIds,
    selectedId,
    selectAnnotation,
    toggleAnnotation,
    selectAnnotations,
    onAnnotationsChange,
    labelSelectedAnnotation,
    deleteSelectedAnnotation,
    undoLastLabel,
    canUndo,
  };
};
